import { Procedure } from '../define-procedure';
import { Store } from '@ngxs/store';
import { RPCEvent } from '../define-event';
import { MessageEnum } from '../models/message.enum';
import { LabelsService } from 'src/app/story/labels.service';
import { XYZModel } from 'src/app/shared/models/xyz.model';
import { StoryLabelsState } from 'src/app/story/state/labels/story.labels.state';
import { LabelModel } from 'src/app/story/models/labels/label.model';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { ChapterModel } from 'src/app/story/models/chapter.model';
import { RemoveLabel, AddLabel, SetLabel, SetLabelsShown, SetAllLabelsShown } from 'src/app/story/state/labels/story.labels.actions';
import { HotspotTypeEnum } from 'src/app/story/models/labels/hotspot.types.enum';
import { HotspotModel } from 'src/app/story/models/labels/hotspot.model';
import { LabelPinType } from 'src/app/story/models/labels/label-pin-type.enum';
import { LabelThemeEnum } from 'src/app/story/models/labels/label-theme.enum';
import { omitBy, isNil } from 'lodash';
import { HumanInterface } from '../models/human.interface';
import { MarkerModel } from 'src/app/story/models/marker.model';
import { EventsEnum } from 'src/app/engine/models/events.enum';
import { HotspotsIO } from '../models/hotspots.io';

/* 
  TODO[AC]
  - cache user defined hotspot data
  - to be deprecated: should be maintained by client
*/
const dataCache: { [key: string]: object; } = {};
function addHotspotData(id: string, hotspot: HotspotsIO.APIHotspotModel) {
  if (hotspot.hasOwnProperty('data')) {
    hotspot[id] = hotspot.data;
  }
}

/*
Notes:
- Hotspot IDs are numbers (int) instead of pass-through string IDs.
- Needs to be converted and tracked. label id (string) -> hotspot id (number) | hotspot id (number) -> label id (string)
- IDs are incremented on creation, querying etc.., to prevent collisions.
*/

const HotspotManager = {
  // incremental hotspot id
  hotspotSeedId: 0,

  // map of label id (string) -> hotspot id (number)
  hotspotIdMap: new Map<string, number>(),

  getHotspotMap: (store: Store) => {
    const { hotspotIdMap, createHotspotId } = HotspotManager;
    // side effect: ensure global id map is updated
    const labels = store.selectSnapshot(StoryLabelsState.getLabels);
    labels.forEach((label) => {
      const labelId = label.id;
      const hotspotId = hotspotIdMap.get(labelId);
      if (typeof (hotspotId) === 'undefined') {
        createHotspotId(labelId);
      }
    });
    return hotspotIdMap;
  },

  createHotspotId: (labelId: string) => {
    // increament seed + add entry
    HotspotManager.hotspotSeedId++;
    HotspotManager.hotspotIdMap.set(labelId, HotspotManager.hotspotSeedId);
    return HotspotManager.hotspotSeedId;
  },

  getHotspotIdByLabel: (store: Store, input: string, createEntry?: boolean) => {
    const { getHotspotMap, createHotspotId } = HotspotManager;
    const idMap = getHotspotMap(store);
    const hotspotId = idMap.get(input);
    // create entry if needed
    return createEntry && typeof (hotspotId) === 'undefined' ? createHotspotId(input) : hotspotId;
  },

  getLabelIdByHotspotId: (store: Store, input: number) => {
    const idMap = HotspotManager.getHotspotMap(store);
    const foundPair = Array.from(idMap)
      .find(([lablId, hotspotId]) => hotspotId === input);

    return foundPair ? foundPair[0] : null;
  }
};

function getLabels(store: Store, chapterId?: string): LabelModel[] {
  const labels = store.selectSnapshot(StoryLabelsState.getLabels);
  return typeof (chapterId) !== 'undefined'
    ? labels.filter((label) => label.chapterId === chapterId) // filter by chapter
    : labels; // all labels
}

function getLabelByHotspotId(store: Store, hotspotId: number): LabelModel {
  const labels = getLabels(store);
  const labelId = HotspotManager.getLabelIdByHotspotId(store, hotspotId);
  return labels.find((label) => label.id === labelId);
}

function getMarkerById(human: HumanInterface, labelId: string): MarkerModel {
  return human.scene.markers.markers[labelId];
}

function convertHotspotToLabel(store: Store, labelsService: LabelsService, hotspot: HotspotsIO.APIHotspotModel): LabelModel {
  const { title, objectId, position, direction, chapterScope, type } = hotspot;
  const chapters = store.selectSnapshot(StoryChaptersState.getStoryChapters);
  const chapter = store.selectSnapshot(StoryChaptersState.getSelectedChapter);
  const chapterIndex = store.selectSnapshot(StoryChaptersState.getSelectedChapterIndex);

  
  const targetChapterIndex = typeof (chapterScope) === 'number' ? chapterScope : chapterIndex;
  const targetChapter = chapters[targetChapterIndex] || chapter;
  
  // shown state
  const shown = hotspot.shown !== false;
  const labelShown = shown && !!title;

  const hotspotModel: HotspotModel = {
    title: title,
    type: type ? type : HotspotTypeEnum.CircleLabel,
    objectId: objectId,
    pos: position ? fromXYZ(position) : null,
    dir: direction ? fromXYZ(direction) : null,
    shown: shown,
    symbol: '',
    className: '',
  };

  // convert to app style label
  const label = labelsService.deserializeHotspots({}, targetChapter, targetChapterIndex, hotspotModel);
  label.title = title;

  label.expandTag = true;
  label.flyTo = false;
  label.flyToTarget = null;
  label.occludable = true;
  label.pinGlow = false;
  label.pinType = LabelPinType.PlusMinus;

  // initial label shown state
  label.shown = shown;
  label.hideTag = !labelShown;
  label.hideWire = true;

  // set themes
  label.theme = LabelThemeEnum.StudioNext;
  // set pin type
  label.pinType = convertHotspotPinTypeToLabelPinType(type);

  // #TODO[AC]: cache client data
  addHotspotData(label.id, hotspot);

  return label;
}

function convertHotspotPinTypeToLabelPinType(hotspotType: HotspotTypeEnum): LabelPinType {
  if (hotspotType === HotspotTypeEnum.CirclePlus) {
    return LabelPinType.Plus;
  } else if (hotspotType === HotspotTypeEnum.CircleMinus) {
    return LabelPinType.Minus;
  } else {
    return LabelPinType.Default;
  }
}

function convertLabelIdToHotspotId(store: Store, labelId: string): number {
  // get hotspot id
  return HotspotManager.getHotspotIdByLabel(store, labelId);
}

// utility to normalize string id properties to number id properties
function convertLabelResponseToHotspotResponse<Response extends
  HotspotsIO.HotspotsEventResponse>(store: Store, input: HotspotsIO.IIdModel<string>): Response {
  return <Response>{
    ...input,
    // string id -> number id
    id: convertLabelIdToHotspotId(store, input.id)
  };
}

function convertLabelPositionToHotspotPosition(store: Store, label: HotspotsIO.LabelPositionResponse): HotspotsIO.HotspotPositionResponse {
  const { id, canvasPos } = label;
  const { x, y } = canvasPos;
  return {
    id: convertLabelIdToHotspotId(store, id),
    canvasPosition: {
      0: x,
      1: y
    }
  };
}

function mergeLabelAndHotspot(hotspot: HotspotsIO.APIHotspotModel, label: LabelModel): LabelModel {
  const { title, objectId, direction, position, shown, type } = hotspot;
  const extensions = {
    ...label,
    title,
    objectId,
    shown,
    pos: position ? fromXYZ(position) : null,
    dir: direction ? fromXYZ(direction) : null,
    pinType: type ? convertHotspotPinTypeToLabelPinType(type) : null
  };

  // #TODO[AC]: cache client data
  addHotspotData(label.id, hotspot);

  return {
    ...label, // copy base properties
    ...omitBy(extensions, isNil) // trim null extended properties
  };
}

function convertLabelToHotspot(human: HumanInterface, store: Store, chapters: ChapterModel[], label: LabelModel): HotspotsIO.APIHotspotModel {
  // get label props
  const { id, chapterId, title, objectId } = label;
  // get chapter scope
  const chapterIndex = chapters.findIndex((chapter) => chapterId === chapter.id);

  // get mapped hotspot ID, else create
  const hotspotId = HotspotManager.getHotspotIdByLabel(store, id, true);
  const marker = getMarkerById(human, id);

  return {
    id: hotspotId,
    title: title,
    objectId: objectId,
    position: marker ? toXYZ(marker.pos) : null,
    direction: marker ? toXYZ(marker.dir) : null,
    canvasPosition: marker ? toXY(marker.canvasPos) : null,
    chapterScope: chapterIndex,
    type: label.pinType === LabelPinType.PlusMinus ? HotspotTypeEnum.CirclePlus : HotspotTypeEnum.Circle,
    shown: label.shown,
    data: dataCache[label.id]
  };
}

function convertLabelsToHotspots(human: HumanInterface, store: Store, labels: LabelModel[]): HotspotsIO.APIHotspotModel[] {
  const chapters = store.selectSnapshot(StoryChaptersState.getStoryChapters);
  return labels.map((label) => convertLabelToHotspot(human, store, chapters, label));
}

function toXYZ(input: number[] | Float32Array): XYZModel {
  return { x: input[0], y: input[1], z: input[2] };
}

function fromXYZ(input: XYZModel): [number, number, number] {
  return [input.x, input.y, input.z];
}

function toXY(input: number[] | Float32Array): HotspotsIO.XYModel {
  return { x: input[0], y: input[1] };
}

function getHotspotTypes() {
  return Object.values(HotspotTypeEnum)
    .map((type) => `${type}`);
}

function createLabel(store: Store, label: LabelModel): LabelModel {
  // validate required fields for label creation
  if (label.objectId || label.pos) {
    store.dispatch(new AddLabel(label));
    return label;
  } else {
    return null;
  }
}

function updateLabel(store: Store, label: LabelModel): LabelModel {
  store.dispatch(new SetLabel(label));
  return label;
}

function destroyLabel(store: Store, label: LabelModel): LabelModel {
  store.dispatch(new RemoveLabel(label));
  return label;
}

function convertLabelFromHotspot(store: Store, hotspot: HotspotsIO.APIHotspotModel): LabelModel {
  // get label
  const label = getLabelByHotspotId(store, hotspot.id);
  if (label) {
    // update label from hotspot
    return mergeLabelAndHotspot(hotspot, label);
  } else {
    return null;
  }
}

function setLabelsShown(store: Store, labels: LabelModel[], shown: boolean): true {
  store.dispatch(new SetLabelsShown(labels, shown));
  return true;
}

function getLabelsFromHotspotFilter(store: Store, hotspotFilter?: HotspotsIO.HotspotFilter): LabelModel[] {
  if (typeof (hotspotFilter) === 'number') {
    const label = getLabelByHotspotId(store, hotspotFilter);
    return label ? [label] : [];
  } else {
    return getLabels(store);
  }
}

export function getHotspotsEvents(human: HumanInterface, store: Store): RPCEvent<HotspotsIO.HotspotsEventResponse>[] {
  return [
    {
      eventName: EventsEnum.HotspotsEntered,
      mapFn: (response: HotspotsIO.LabelIdResponse) => convertLabelResponseToHotspotResponse<HotspotsIO.HotspotIdResponse>(store, response)
    },
    {
      eventName: EventsEnum.HotspotsLeft,
      mapFn: (response: HotspotsIO.LabelIdResponse) => convertLabelResponseToHotspotResponse<HotspotsIO.HotspotIdResponse>(store, response)
    },
    {
      eventName: EventsEnum.HotspotsPicked,
      mapFn: (response: HotspotsIO.LabelIdResponse) => convertLabelResponseToHotspotResponse<HotspotsIO.HotspotIdResponse>(store, response)
    },
    {
      eventName: EventsEnum.HotspotsDisplayUpdated,
      mapFn: (response: HotspotsIO.LabelDisplayResponse) => convertLabelResponseToHotspotResponse<HotspotsIO.HotspotDisplayResponse>(store, response)
    },
    {
      eventName: EventsEnum.HotspotsPositionUpdated,
      mapFn: (response: HotspotsIO.LabelPositionResponse) => convertLabelPositionToHotspotPosition(store, response)
    }
  ];
}

export function getHotspotsProcedures(
  human: HumanInterface,
  store: Store,
  labelsService: LabelsService
): Procedure<HotspotsIO.HotspotsProcRequest, HotspotsIO.HotspotsProcResponse>[] {
  return [
    {
      procName: MessageEnum.HotspotsInfo,
      actionFn: (hotspotId?: HotspotsIO.HotspotFilter) => {
        
        // if no param is sent by the client,
        // the RPC layer passes along an empty object, instead of undefined :/
        const sanitizedHotspotId = typeof hotspotId === 'number'
          ? hotspotId 
          : undefined;

        // Mimic existing API:
        // if a hotspotId is requested, just return that hotspot.
        if (sanitizedHotspotId !== undefined) {
          const label = getLabelByHotspotId(store, sanitizedHotspotId);
          const hotspot = convertLabelsToHotspots(human, store, [label])[0];
          return hotspot;
        } else {

          // if no hotspotId is requested, return an array of hotspots.
          const labels = getLabelsFromHotspotFilter(store, sanitizedHotspotId);
          const hotspots = convertLabelsToHotspots(human, store, labels);
          return hotspots;
        }
      }
    },
    {
      procName: MessageEnum.HotspotsTypes,
      mapFnIn: () => getHotspotTypes(),
      actionFn: (types: string[]) => types
    },
    {
      procName: MessageEnum.HotspotsCreate,
      mapFnIn: (hotspot: HotspotsIO.APIHotspotModel) => convertHotspotToLabel(store, labelsService, hotspot),
      actionFn: (label: LabelModel) => createLabel(store, label),
      mapFnOut: (label: LabelModel) => label ? convertLabelIdToHotspotId(store, label.id) : null
    },
    {
      procName: MessageEnum.HotspotsUpdate,
      mapFnIn: (hotspot: HotspotsIO.APIHotspotModel) => convertLabelFromHotspot(store, hotspot),
      actionFn: (label: LabelModel) => label ? updateLabel(store, label) : null
    },
    {
      procName: MessageEnum.HotspotsDestroy,
      mapFnIn: (hotspotId?: number) => getLabelByHotspotId(store, hotspotId),
      actionFn: (label: LabelModel) => label ? destroyLabel(store, label) : null
    },
    {
      procName: MessageEnum.HotspotsShow,
      mapFnIn: (hotspotFilter: HotspotsIO.HotspotFilter) => getLabelsFromHotspotFilter(store, hotspotFilter),
      actionFn: (labels: LabelModel[]) => setLabelsShown(store, labels, true)
    },
    {
      procName: MessageEnum.HotspotsHide,
      mapFnIn: (hotspotFilter: HotspotsIO.HotspotFilter) => getLabelsFromHotspotFilter(store, hotspotFilter),
      actionFn: (labels: LabelModel[]) => setLabelsShown(store, labels, false)
    }
  ];
}
